import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';

import './index.css';
import Layout from './components/common/Layout';
import ProtectedRoute from './components/layout/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import Spinner from './components/common/Spinner';
import ProfileCompletionCheck from './components/profile/ProfileCompletionCheck';
import { Toaster } from './components/ui/sonner';
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const SignUpPage = lazy(() => import('./pages/SignUpPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword'));
const HomePage = lazy(() => import('./pages/HomePage'));
const RoomsPage = lazy(() => import('./pages/RoomsPage'));
const MessPage = lazy(() => import('./pages/MessPage'));
const RoomDetailPage = lazy(() => import('./pages/RoomDetailPage'));
const MessDetailPage = lazy(() => import('./pages/MessDetailPage'));
const OwnerRoomsPage = lazy(() => import('./pages/OwnerRoomsPage'));
const OwnerMessPage = lazy(() => import('./pages/OwnerMessPage'));

// NEW: Profile Completion Page
const ProfileCompletePage = lazy(() => import('./components/profile/ProfileCompletePage'));
const ProfileViewPage = lazy(() => import('./components/profile/ProfileViewPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));

// Lazy Load Admin Components
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const ApproveListings = lazy(() => import('./components/admin/ApproveListings'));
const UserManagement = lazy(() => import('./components/admin/UserManagement'));
const BookingsManagement = lazy(() => import('./components/admin/BookingsManagement'));
const ApproveOwnerProfiles = lazy(() => import('./components/admin/ApproveOwnerProfiles'));

// Lazy Load Listing/Booking Components
const CreateRoomForm = lazy(() => import('./components/listings/CreateRoomForm'));
const CreateMessForm = lazy(() => import('./components/listings/CreateMessForm'));
const OwnerBookings = lazy(() => import('./components/listings/OwnerBookings'));
const StudentBookings = lazy(() => import('./components/listings/StudentBookings'));

// 404 Page
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Dashboard Redirect Component
const DashboardRedirect = lazy(() => import('./components/common/DashboardRedirect'));

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <Suspense fallback={<div className="flex justify-center items-center h-screen"><Spinner /></div>}>
          <Routes>
            {/* === PUBLIC ROUTES === */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />

            {/* === PUBLIC & PROTECTED ROUTES WITH LAYOUT === */}
            <Route element={<Layout />}>
              {/* Public pages */}
              <Route path="/homepage" element={<HomePage />} />
              <Route path="/rooms" element={<RoomsPage />} />
              <Route path="/mess" element={<MessPage />} />
              <Route path="/rooms/:itemId" element={<RoomDetailPage />} />
              <Route path="/mess/:itemId" element={<MessDetailPage />} />

              {/* Other page route */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/help" element={<HelpPage />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* PROFILE COMPLETION PAGE — NEW ROUTE */}
              <Route
                path="/profile/edit"
                element={
                  <ProtectedRoute roles={['student', 'owner']}>
                    <ProfileCompletePage />
                  </ProtectedRoute>
                }
              />

              {/* PROFILE VIEW PAGE — NEW */}
              <Route
                path="/profile/view"
                element={
                  <ProtectedRoute roles={['student', 'owner']}>
                    <ProfileCompletionCheck>
                      <ProfileViewPage />
                    </ProfileCompletionCheck>
                  </ProtectedRoute>
                }
              />

              {/* // In your Routes component */}
              <Route
                path="/settings"
                element={
                  <ProtectedRoute roles={['student', 'owner']}>
                    <ProfileCompletionCheck>
                      <SettingsPage />
                    </ProfileCompletionCheck>
                  </ProtectedRoute>
                }
              />
              {/* Owner-specific routes */}
              <Route
                path="/dashboard/bookings"
                element={
                  <ProtectedRoute roles={['owner']}>
                    <ProfileCompletionCheck>
                      <OwnerBookings />
                    </ProfileCompletionCheck>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/owner/rooms"
                element={
                  <ProtectedRoute roles={['owner']}>
                    <ProfileCompletionCheck>
                      <OwnerRoomsPage />
                    </ProfileCompletionCheck>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/owner/mess"
                element={
                  <ProtectedRoute roles={['owner']}>
                    <ProfileCompletionCheck>
                      <OwnerMessPage />
                    </ProfileCompletionCheck>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/create-room"
                element={
                  <ProtectedRoute roles={['owner']}>
                    <ProfileCompletionCheck>
                      <CreateRoomForm />
                    </ProfileCompletionCheck>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/create-mess"
                element={
                  <ProtectedRoute roles={['owner']}>
                    <ProfileCompletionCheck>
                      <CreateMessForm />
                    </ProfileCompletionCheck>
                  </ProtectedRoute>
                }
              />

              {/* Student-specific routes */}
              <Route
                path="/student/bookings"
                element={
                  <ProtectedRoute roles={['student']}>
                    <ProfileCompletionCheck>
                      <StudentBookings />
                    </ProfileCompletionCheck>
                  </ProtectedRoute>
                }
              />

              {/* Admin routes — NO profile check */}
              <Route path="/admin/dashboard" element={<ProtectedRoute roles={['admin']}><AdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/listings" element={<ProtectedRoute roles={['admin']}><ApproveListings /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute roles={['admin']}><UserManagement /></ProtectedRoute>} />
              <Route path="/admin/bookings" element={<ProtectedRoute roles={['admin']}><BookingsManagement /></ProtectedRoute>} />
              <Route path="/admin/owners" element={<ProtectedRoute roles={['admin']}><ApproveOwnerProfiles /></ProtectedRoute>} />
            </Route>

            {/* Generic Dashboard Route - Redirect based on user role */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardRedirect />
                </ProtectedRoute>
              } />

            {/* Root route redirect */}
            <Route path="/" element={<Navigate to="/homepage" replace />} />

            {/* 404 Route */}
            <Route path="/404" element={<NotFoundPage />} />

            {/* Catch-all 404 for unmatched routes */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
      <Toaster richColors position="bottom-right" />
    </AuthProvider>
  );
}

export default App;